/**
 * PDP delivery line: "Arrives by Thu, Dec 18 if ordered in the next 5 hrs 12 min."
 *
 * Mirrors the cart-drawer shipment scheduler's "Ship now" date: the first ship day
 * (per the theme's ship days and blackout dates) whose cutoff hasn't passed, plus the
 * maximum lead time. The countdown runs to that ship day's cutoff, so on a Saturday it
 * counts down to Monday's cutoff rather than a cutoff that won't ship anything.
 *
 * All date math is done in shop time. Dates here are "wall clock" dates: their UTC
 * fields hold the shop's local date and time, which keeps the math independent of the
 * shopper's own time zone.
 */
class PdpDeliveryEstimate extends HTMLElement {
  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    this.#render();
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
  }

  #render = () => {
    clearTimeout(this.#timer);
    const config = this.#config();
    const now = new Date(Date.now() + config.offsetMs);

    const ship = startOfDay(now);
    if (minutesOfDay(now) >= config.cutoffMinutes) ship.setUTCDate(ship.getUTCDate() + 1);
    let guard = 0;
    while (!isShippable(ship, config) && guard < 365) {
      ship.setUTCDate(ship.getUTCDate() + 1);
      guard++;
    }
    if (guard >= 365) {
      this.replaceChildren();
      return;
    }

    const deadline = new Date(ship.getTime() + config.cutoffMinutes * 60_000);
    const arrives = new Date(ship);
    arrives.setUTCDate(arrives.getUTCDate() + config.leadMax);

    this.innerHTML = `Arrives by <strong>${formatDate(arrives)}</strong> if ordered in the next <strong>${formatRemaining(
      deadline.getTime() - now.getTime()
    )}</strong>.`;

    // Tick on the next minute boundary so the countdown never shows a stale minute.
    this.#timer = window.setTimeout(this.#render, 60_000 - (Date.now() % 60_000) + 50);
  };

  #config() {
    const ds = this.dataset;
    const [hh, mm] = (ds.cutoff || '14:00').split(':').map((n) => parseInt(n, 10));
    let blackouts = [];
    try {
      blackouts = JSON.parse(ds.blackouts || '[]');
    } catch (e) {
      blackouts = [];
    }
    return {
      leadMax: parseInt(ds.leadMax, 10) || 0,
      cutoffMinutes: (hh || 0) * 60 + (mm || 0),
      offsetMs: parseOffset(ds.utcOffset),
      shipDays: (ds.shipDays || '').split(',').map((s) => s.trim() === 'true'),
      blackouts: new Set(blackouts),
    };
  }
}

/**
 * "-0400" → milliseconds. Falls back to the browser's own offset if missing.
 * @param {string | undefined} value
 */
function parseOffset(value) {
  const match = /^([+-])(\d{2})(\d{2})$/.exec((value || '').trim());
  if (!match) return -new Date().getTimezoneOffset() * 60_000;
  const minutes = parseInt(match[2], 10) * 60 + parseInt(match[3], 10);
  return (match[1] === '-' ? -minutes : minutes) * 60_000;
}

/** @param {Date} d */
function startOfDay(d) {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

/** @param {Date} d */
function minutesOfDay(d) {
  return d.getUTCHours() * 60 + d.getUTCMinutes();
}

/** @param {Date} d */
function formatIso(d) {
  return d.toISOString().slice(0, 10);
}

function isShippable(date, config) {
  if (!config.shipDays[date.getUTCDay()]) return false;
  if (config.blackouts.has(formatIso(date))) return false;
  return true;
}

/** @param {Date} d */
function formatDate(d) {
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/** @param {number} ms */
function formatRemaining(ms) {
  const totalMinutes = Math.max(1, Math.floor(ms / 60_000));
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  const plural = (n, unit) => `${n} ${unit}${n === 1 ? '' : 's'}`;

  if (days > 0) return hours > 0 ? `${plural(days, 'day')} ${plural(hours, 'hr')}` : plural(days, 'day');
  if (hours > 0) return `${plural(hours, 'hr')} ${minutes} min`;
  return `${minutes} min`;
}

if (!customElements.get('pdp-delivery-estimate')) {
  customElements.define('pdp-delivery-estimate', PdpDeliveryEstimate);
}
